import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Users, Clock, Briefcase, DollarSign, Plus, X, Phone, Mail, CheckCircle2, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import Swal from 'sweetalert2';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth';

const EmployeeRoster = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [page, setPage] = useState(1);
    const { register, handleSubmit, reset } = useForm();
    
    const axiosSecure = UseAxiosSecure();
    const queryClient = useQueryClient();
    const { activeBranchId } = UseAuth();

    // 1. FETCH ALL BRANCHES (For the Transfer Dropdown)
    const { data: branches = [] } = useQuery({
        queryKey: ['all-branches'],
        queryFn: async () => {
            const res = await axiosSecure.get('/branch'); // Adjust this endpoint if your get-all-branches route is different
            return res.data?.data || [];
        }
    });

    // 2. FETCH PAGINATED HR EMPLOYEES
    const { data: queryData, isLoading: empLoading } = useQuery({
        queryKey: ['hr-employees', activeBranchId, page],
        queryFn: async () => {
            const res = await axiosSecure.get('/hr/employees', {
                params: { page, limit: 9 }
            });
            return res.data?.data || {};
        },
        enabled: !!activeBranchId,
        keepPreviousData: true
    });

    const employees = queryData?.employees || [];
    const totalPages = queryData?.totalPages || 1;
    const totalEmployees = queryData?.totalEmployees || 0;

    // 3. FETCH USERS (For the Add Employee dropdown)
    const { data: usersData } = useQuery({
        queryKey: ['users-list'],
        queryFn: async () => {
            const res = await axiosSecure.get('/users?limit=100');
            return res.data?.data?.users || [];
        }
    });

    // 4. CREATE EMPLOYEE MUTATION
    const createEmployeeMutation = useMutation({
        mutationFn: async (data) => {
            const res = await axiosSecure.post('/hr/employee', data);
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['hr-employees', activeBranchId]);
            setIsModalOpen(false);
            reset();
            Swal.fire({ icon: 'success', title: 'HR Profile Created!', background: '#1a103c', color: '#fff', timer: 1500, showConfirmButton: false });
        }
    });

    // 5. CLOCK IN / OUT MUTATION
    const toggleAttendanceMutation = useMutation({
        mutationFn: async (employeeId) => {
            const res = await axiosSecure.post('/hr/attendance/toggle', { employeeId });
            return res.data;
        },
        onSuccess: (data) => {
            Swal.fire({ icon: 'success', title: 'Time Clock Updated', text: data.message, background: '#1a103c', color: '#fff', timer: 2000, showConfirmButton: false });
        }
    });

    // 6. ASSIGN / TRANSFER BRANCH MUTATION
    const assignBranchMutation = useMutation({
        mutationFn: async ({ targetUserId, newBranchId }) => {
            const res = await axiosSecure.post('/hr/assign-branch', { targetUserId, newBranchId });
            return res.data;
        },
        onSuccess: () => {
            // Instantly refresh the grid so the transferred employee disappears from the current branch view
            queryClient.invalidateQueries(['hr-employees', activeBranchId]);
            Swal.fire({ icon: 'success', title: 'Transferred!', text: 'Employee moved to new location.', background: '#1a103c', color: '#fff', timer: 1500, showConfirmButton: false });
        },
        onError: (error) => {
            Swal.fire({ icon: 'error', title: 'Failed', text: error.response?.data?.message || 'Could not transfer employee.', background: '#1a103c', color: '#fff' });
        }
    });

    const onSubmit = (data) => {
        createEmployeeMutation.mutate({ ...data, baseSalary: Number(data.baseSalary) });
    };

    if (!activeBranchId) return <div className="text-white text-center mt-20">Loading HR System...</div>;

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            
            {/* --- HEADER --- */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Users className="text-pink-500" /> Staff Directory
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">Manage {totalEmployees} active HR profiles and track shifts.</p>
                </div>
                <button 
                    onClick={() => setIsModalOpen(true)}
                    className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg active:scale-95 whitespace-nowrap"
                >
                    <Plus size={18} /> Add Employee
                </button>
            </div>

            {/* --- EMPLOYEES GRID --- */}
            {empLoading ? (
                <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div></div>
            ) : employees.length === 0 ? (
                <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl p-12 text-center text-slate-500 flex flex-col items-center">
                    <Briefcase size={64} className="mb-4 opacity-20 text-purple-500" />
                    <h3 className="text-xl font-bold text-white mb-2">No Staff Added</h3>
                    <p>Create an HR profile for your users to start tracking payroll.</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {employees.map((emp) => (
                            <div key={emp._id} className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 hover:border-white/10 transition-colors rounded-3xl p-6 shadow-xl relative group flex flex-col justify-between">
                                <div>
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-3">
                                            <img src={emp.userId?.profileImage || `https://ui-avatars.com/api/?name=${emp.userId?.name}&background=random`} alt={emp.userId?.name} className="w-12 h-12 rounded-xl object-cover border-2 border-white/10" />
                                            <div>
                                                <h3 className="font-bold text-white text-lg truncate max-w-[120px]">{emp.userId?.name}</h3>
                                                <p className="text-xs text-slate-400 flex items-center gap-1"><Mail size={12}/> {emp.userId?.email}</p>
                                            </div>
                                        </div>
                                        <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${emp.status === 'Active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                                            {emp.status}
                                        </span>
                                    </div>

                                    <div className="space-y-3 mb-6 bg-black/20 p-4 rounded-2xl border border-white/5">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-400 flex items-center gap-1"><Briefcase size={14}/> Role</span>
                                            <span className="text-white font-bold">{emp.jobTitle}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-400 flex items-center gap-1"><DollarSign size={14}/> Pay Rate</span>
                                            <span className="text-pink-400 font-bold">${emp.baseSalary} / {emp.salaryType === 'Hourly' ? 'hr' : 'mo'}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-400 flex items-center gap-1"><Phone size={14}/> Phone</span>
                                            <span className="text-white">{emp.phone || 'N/A'}</span>
                                        </div>

                                        {/* --- 🚀 NEW: BRANCH TRANSFER DROPDOWN --- */}
                                        <div className="flex justify-between items-center text-sm pt-3 mt-3 border-t border-white/5">
                                            <span className="text-slate-400 flex items-center gap-1"><Building2 size={14}/> Location</span>
                                            <select 
                                                onChange={(e) => assignBranchMutation.mutate({ targetUserId: emp.userId?._id, newBranchId: e.target.value })}
                                                defaultValue={activeBranchId} // Defaults to the current view
                                                disabled={assignBranchMutation.isLoading}
                                                className="bg-[#0f0a1f] border border-white/10 rounded-lg text-xs font-bold text-white p-1.5 focus:outline-none focus:border-pink-500 max-w-[120px] truncate cursor-pointer hover:bg-white/5 transition-colors"
                                            >
                                                {branches.map(branch => (
                                                    <option key={branch._id} value={branch._id}>
                                                        {branch.branchName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <button onClick={() => toggleAttendanceMutation.mutate(emp._id)} className="w-full py-3 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 font-bold rounded-xl border border-cyan-500/20 flex items-center justify-center gap-2 transition-colors mt-auto">
                                    <Clock size={18} /> Simulate Clock In/Out
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* --- PAGINATION CONTROLS --- */}
                    <div className="flex justify-between items-center bg-[#1a103c]/40 backdrop-blur-md p-4 rounded-2xl border border-white/5 mt-6">
                        <span className="text-sm text-slate-400 font-medium">Page {page} of {totalPages}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-2 bg-white/5 rounded-xl disabled:opacity-30 hover:bg-white/10 text-white transition-colors">
                                <ChevronLeft size={20}/>
                            </button>
                            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-2 bg-white/5 rounded-xl disabled:opacity-30 hover:bg-white/10 text-white transition-colors">
                                <ChevronRight size={20}/>
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* --- ADD EMPLOYEE MODAL --- */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
                    <div className="relative w-full max-w-lg bg-[#1a103c] border border-white/10 rounded-[2rem] shadow-2xl overflow-hidden p-8">
                        <button onClick={() => setIsModalOpen(false)} className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors"><X size={24} /></button>
                        <h3 className="text-2xl font-bold text-white mb-6">Create HR Profile</h3>
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Link to System Account</label>
                                <select {...register("userId", { required: true })} className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-pink-500">
                                    <option value="">Select an existing user...</option>
                                    {usersData?.map(u => <option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Job Title</label>
                                    <input {...register("jobTitle", { required: true })} type="text" placeholder="e.g. Head Chef" className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-pink-500" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Phone</label>
                                    <input {...register("phone")} type="text" placeholder="Contact number" className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-pink-500" />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-1 col-span-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Type</label>
                                    <select {...register("employmentType")} className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-pink-500">
                                        <option value="Full-time">Full-time</option>
                                        <option value="Part-time">Part-time</option>
                                    </select>
                                </div>
                                <div className="space-y-1 col-span-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pay Basis</label>
                                    <select {...register("salaryType")} className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-pink-500">
                                        <option value="Hourly">Hourly</option>
                                        <option value="Monthly">Monthly</option>
                                    </select>
                                </div>
                                <div className="space-y-1 col-span-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Amount ($)</label>
                                    <input {...register("baseSalary", { required: true })} type="number" step="0.01" placeholder="0.00" className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-pink-500" />
                                </div>
                            </div>
                            <button type="submit" className="w-full mt-4 py-4 rounded-xl bg-pink-600 text-white font-bold text-lg hover:bg-pink-500 active:scale-95 transition-all flex items-center justify-center gap-2">
                                <CheckCircle2 size={20} /> Save Employee Profile
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeRoster;